<template>
    <b-modal
        v-model="visible"
        @show="initModal"
        :id="modalId"
        :title="modalTitle"
    >
        <template v-if="!loading">
            <b-form @submit="save">
                <b-form-group>
                    <label for="poInput">{{ $t("PO Number") }}</label>
                    <b-form-input
                        ref="poNumber"
                        id="poInput"
                        v-model="po_number"
                        required
                        :state="errors.po_number === undefined ? null : false"
                        :placeholder="$t('Fill in the PO number')"
                    >
                    </b-form-input>
                    <div
                        class="invalid-feedback"
                        v-for="error in errors.po_number"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </b-form-group>

                <b-form-group v-if="adminMode">
                    <label for="clientSelect">{{ $t("Client") }}</label>
                    <b-form-select
                        @change="updateManufacturers"
                        id="clientSelect"
                        :options="clients"
                        v-model="client_id"
                        :state="errors.client_id === undefined ? null : false"
                    >
                    </b-form-select>
                    <div
                        class="invalid-feedback"
                        v-for="error in errors.client_id"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </b-form-group>

                <b-form-group>
                    <label for="manufacturerSelect">{{
                        $t("Manufacturer")
                    }}</label>
                    <b-form-select
                        id="manufacturerSelect"
                        :options="manufacturers"
                        v-model="manufacturer_id"
                        :state="errors.manufacturer_id === undefined ? null : false"
                    >
                    </b-form-select>
                    <div
                        class="invalid-feedback"
                        v-for="error in errors.manufacturer_id"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </b-form-group>

                <b-form-group>
                    <label for="finalClient">{{ $t("Final Client") }}</label>
                    <b-form-input
                        ref="finalClient"
                        id="finalClient"
                        v-model="final_client"
                        required
                        :state="errors.final_client === undefined ? null : false"
                        :placeholder="$t('Fill in the Final Client')"
                    >
                    </b-form-input>
                    <div
                        class="invalid-feedback"
                        v-for="error in errors.final_client"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </b-form-group>

                <b-row>
                    <b-col>
                        <b-form-group>
                            <label for="orderDateInput">{{ $t("Order date") }}</label>
                            <b-form-input
                                id="orderDateInput"
                                v-model="order_date"
                                required
                                type="date"
                                max="2999-01-01"
                                :placeholder="$t('Pick a date for this order')"
                                :state="errors.order_date === undefined ? null : false"
                            >
                            </b-form-input>
                            <div
                                class="invalid-feedback"
                                v-for="error in errors.order_date"
                                :key="error"
                            >
                                {{ error }}
                            </div>
                        </b-form-group>
                    </b-col>

                    <b-col>
                        <b-form-group>
                            <label for="dueDateInput">{{ $t("Due date") }}</label>
                            <b-form-input
                                id="dueDateInput"
                                v-model="due_date"
                                required
                                max="2999-01-01"
                                type="date"
                                :placeholder="$t('Pick a due date for this order')"
                                :state="errors.order_date === undefined ? null : false"
                            >
                            </b-form-input>
                            <div
                                class="invalid-feedback"
                                v-for="error in errors.order_date"
                                :key="error"
                            >
                                {{ error }}
                            </div>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group v-if="needRedirect">
                    <label for="commentTextArea">{{ $t("Comment") }}</label>
                    <b-form-textarea
                        id="commentTextArea"
                        v-model="comment"
                        :state="errors.comment === undefined ? null : false"
                    >
                    </b-form-textarea>
                    <div
                        class="invalid-feedback"
                        v-for="error in errors.comment"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </b-form-group>

                <b-row>
                    <b-col>
                        <b-form-group>
                            <b-form-checkbox
                                id="serialNumber"
                                v-model="serial_number"
                                name="serial_number"
                                v-if="isUpdate"
                            >
                                Serial Number
                            </b-form-checkbox>
                            <div
                                class="invalid-feedback"
                                v-for="error in errors.serial_number"
                                :key="error"
                            >
                                {{ error }}
                            </div>
                        </b-form-group>
                    </b-col>

                    <b-col>
                        <b-form-group>
                            <b-form-checkbox
                                id="isSkipQualityControl"
                                v-model="is_skip_quality_control"
                                name="is_skip_quality_control"
                                v-if="isUpdate"
                            >
                                Skip quality Control
                            </b-form-checkbox>
                            <div
                                class="invalid-feedback"
                                v-for="error in errors.is_skip_quality_control"
                                :key="error"
                            >
                                {{ error }}
                            </div>
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-form>
        </template>

        <template v-else>
            <loading-spinner class="loading-spinner" v-if="loading" />
        </template>

        <template v-slot:modal-footer="{ cancel }">
            <b-spinner label="Saving" small v-if="saving"></b-spinner>
            <b-button size="md" variant="outline-secondary" @click="cancel()">
                {{ $t("Cancel") }}
            </b-button>
            <b-button
                size="md"
                variant="primary"
                @click="save"
                :disabled="saving"
            >
                {{ $t("Save") }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import axios from "axios";
import LoadingSpinner from "../Common/LoadingSpinner";

export default {
  name: "OrdersPurchaseOrder",
  components: { LoadingSpinner },
  props: {
    modalId: {
      type: String,
      default: null
    },
    order: {
      type: Object,
      default: () => {
        return {};
      }
    },
    adminMode: {
      type: Boolean,
      default: false
    },
    needRedirect: {
      type: Boolean,
      default: true
    },
    isUpdate: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showModal: this.visible,
      po_number: null,
      final_client: null,
      manufacturer_id: null,
      comment: null,
      client_id: null,
      order_date: null,
      serial_number: true,
      is_skip_quality_control: false,
      due_date: null,
      saving: false,
      loading: true,
      visible: false,
      manufacturers: [],
      errors: [],
      clients: []
    };
  },
  computed: {
    modalTitle() {
      return this.order.po_number
        ? `Edit order ${this.order.po_number}`
        : "Create new order";
    }
  },
  methods: {
    initModal() {
      this.errors = [];

      if (this.order.id !== undefined) {
        this.po_number = this.order.po_number;
        this.manufacturer_id = this.order.manufacturer.id;
        this.order_date = this.order.order_date;
        this.due_date = this.order.due_date;
        this.client_id = this.order.client.id;
        this.comment = this.order.comment;
        this.final_client = this.order.final_client;
      }
      let promises = [];
      promises.push(this.getManufacturers());

      if (this.adminMode) {
        promises.push(this.getClients());
      }

      Promise.all(promises).finally(() => {
        this.loading = false;

        setTimeout(() => {
          this.$refs.poNumber.focus();
        }, 500);
      });
    },
    save() {
      this.saving = true;

      let p =
        this.order.id !== undefined ? this.updateOrder() : this.createOrder();

      p.finally(() => {
        this.saving = false;
      })
        .then(r => {
          this.saving = false;
          this.visible = false;
          this.$emit("saved");
          if (this.needRedirect) {
            window.location = `/orders/${r.data.data.id}/products`;
          } else if (this.isUpdate) {
            this.$root.$emit("orderEmit", r.data.data);
          }
        })
        .catch(err => {
          this.errors = err.response.data.errors;
        });
    },
    updateOrder() {
      return axios.put("/api/orders/" + this.order.id, {
        po_number: this.po_number,
        manufacturer_id: this.manufacturer_id,
        order_date: this.order_date,
        due_date: this.due_date,
        client_id: this.client_id,
        comment: this.comment,
        final_client: this.final_client,
        is_serial_number: this.serial_number
      });
    },
    createOrder() {
      return axios.post("/api/orders", {
        po_number: this.po_number,
        manufacturer_id: this.manufacturer_id,
        order_date: this.order_date,
        due_date: this.due_date,
        client_id: this.client_id,
        comment: this.comment,
        final_client: this.final_client,
        is_serial_number: this.serial_number,
        is_skip_quality_control: this.is_skip_quality_control
      });
    },
    updateManufacturers() {
      this.manufacturer_id = null;
      this.getManufacturers();
    },
    getManufacturers() {
      if (!this.client_id) {
        return Promise.resolve();
      }
      return axios
        .get(`/api/clients/${this.client_id}/manufacturers`)
        .then(resp => {
          let manufacturers = [];
          manufacturers.push({
            value: null,
            text: "Pick a manufacturer"
          });
          resp.data.data.forEach(m => {
            manufacturers.push({
              value: m.id,
              text: m.name
            });
          });
          this.manufacturers = manufacturers;
        });
    },
    getClients() {
      return axios.get("/api/clients").then(resp => {
        let clients = [];
        clients.push({
          value: null,
          text: "Pick a client"
        });
        resp.data.data.forEach(m => {
          clients.push({
            value: m.id,
            text: m.name
          });
        });
        this.clients = clients;
      });
    }
  }
};
</script>

<style scoped>
</style>
